import { LitElement, html, css, PropertyValues, nothing } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { VesselPlanningModel, VoyagePlan } from '../../../src/models/vessel-planning-model';
import { recalcRoutes } from '../../../src/utils/route-utils';
import { VoyageDialogPopupManager } from '../../voyage-dialog-popup/src/voyage-dialog-popup-manager';
import '../../voyage-popup/src/voyage-popup';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import '@maersk-global/mds-components-core/mc-tag';
import { LoadstarModel, LoadstarValues, decodeLoadstarSource, encodeLoadstarSource } from '../../../src/models/loadstar-model';
import { VoyageImoModel } from '../../../src/models/voyage-imo-model';
import { VoyageSchedulesModel } from '../../../src/models/voyage-schedules-model';
import { roundToDigits } from '../../../src/utils/helpers';

@customElement('voy-drafts-gmt-popup')
export class VoyDraftsGMtPopup extends LitElement
{
    @property({type: String})
    get scheduleId(): string
    {
        return this._scheduleId;
    }

    set scheduleId(value: string)
    {
        const oldValue = this._scheduleId;
        this._scheduleId = value;
        this.requestUpdate('scheduleId', oldValue);
        if (this._shown)
        {
            this._readPlan();
        }
    }

    @property({type: Boolean})
    get shown(): boolean
    {
        return this._shown;
    }

    set shown(value: boolean)
    {
        const oldValue = this._shown;
        this._shown = value;
        this.requestUpdate('scheduleId', oldValue);
        if (this._shown)
        {
            this._readPlan();
        }
    }

    static get styles()
    {
        return [
            css`
            :host {
            }

            .container {
                display: grid;
                grid-template-columns: auto auto auto auto;
                grid-column-gap: 30px;
                grid-row-gap: 10px;
                margin: 15px;
                font-family: MaerskText;
                font-size: 14px;
            }

            .trimLink {
                grid-column: span 3;
            }

            input {
                box-sizing: border-box;
                height: 52px;
                border: 1px solid var(--neutrals-grey-06);
                background-color: var(--neutrals-grey-10, white);
                border-radius: 3px;
                font-family: MaerskText;
                font-size: 14px;
                padding-left: 10px;
                padding-right: 10px;
            }

            input:focus-within {
                outline: none;
                /* Drop Shadow Medium */
                box-shadow: 0px 4px 12px rgba(20, 20, 20, 0.06), 0px 2px 4px rgba(20, 20, 20, 0.1);
            }

            input[invalid] {
                border: 1px solid var(--voyage-error-red, red);
            }

            a {
                grid-column: span 3;
                color: var(--primary-blue-05);
                fill: currentColor;
                font-size: 12px;
                text-decoration: none;
            }

            a > svg {
                height: 12px;
            }

            .field-wrapper input {
                width: 100%;
            }

            .error-message {
                color: var(--voyage-error-red, red);
                font-size: 12px;
                margin-top: 4px;
                line-height: 1.3;
            }

            .labelWithTag {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 4px;
            }
            `
        ];
    }

    firstUpdated() {
        this._readPlan();
    }

    render()
    {
        return html`
        <voyage-popup accept-button-text="Save" .shown=${this.shown}
                      @accept=${this._applyChange}
                      @close=${() => this.dispatchEvent(new Event('close'))}
                      header-text="Edit Draft & GMt"
                      .disableAcceptButton=${!this._isValid}
                      .loading=${this._working}>
            <div class="container">
                <div class="labelWithTag">
                    <span>Forward Draft (m)</span>
                    ${this._isForwardDraftFromLoadstar && !this._forwardDraftEdited ? html`<mc-tag label="LoadStar" appearance="info" fit="small"></mc-tag>` : nothing}
                </div>
                <div class="labelWithTag">
                    <span>Aft Draft (m)</span>
                    ${this._isAftDraftFromLoadstar && !this._aftDraftEdited ? html`<mc-tag label="LoadStar" appearance="info" fit="small"></mc-tag>` : nothing}
                </div>
                <div class="labelWithTag">
                    <span>Fluid GM (m)</span>
                    ${this._isGmtFromLoadstar && !this._gmtEdited ? html`<mc-tag label="LoadStar" appearance="info" fit="small"></mc-tag>` : nothing}
                </div>
                <div class="labelWithTag">
                    <span>Solid GM (m)</span>
                    ${this._isSolidGMFromLoadstar && !this._solidGMEdited ? html`<mc-tag label="LoadStar" appearance="info" fit="small"></mc-tag>` : nothing}
                </div>
                <input type="text" inputmode="decimal" ?invalid=${!this._forwardDraftValid && (this._forwardDraftTouched || this._forwardDraftRaw !== '')} required .value=${this._forwardDraftRaw}
                       @input=${ (evt: InputEvent) => { this._forwardDraftEdited = true; this._onForwardDraftInput(evt) } }
                       @blur=${() => this._onForwardDraftBlur()}
                       placeholder="Eg. 11.5">
                <input type="text" inputmode="decimal" ?invalid=${!this._aftDraftValid && (this._aftDraftTouched || this._aftDraftRaw !== '')} required .value=${this._aftDraftRaw}
                       @input=${ (evt: InputEvent) => { this._aftDraftEdited = true; this._onAftDraftInput(evt) } }
                       @blur=${() => this._onAftDraftBlur()}
                       placeholder="Eg. 11.5">
                <div class="field-wrapper">
                    <input type="text" inputmode="decimal" ?invalid=${!this._gmtValid && (this._gmtTouched || this._gmtRaw !== '')}
                           .value=${this._gmtRaw}
                           @input=${(evt: InputEvent) => { this._gmtEdited = true; this._onGmtInput(evt); }}
                           @blur=${() => this._onGmtBlur()}
                           placeholder="Eg. 2.2">
                    ${this._gmtError ? html`<div class="error-message">${this._gmtError}</div>` : nothing}
                </div>
                <div class="field-wrapper">
                    <input type="text" inputmode="decimal" ?invalid=${!this._solidGMValid && (this._solidGMTouched || this._solidGMRaw !== '')}
                           .value=${this._solidGMRaw}
                           @input=${(evt: InputEvent) => { this._solidGMEdited = true; this._onSolidGMInput(evt); }}
                           @blur=${() => this._onSolidGMBlur()}
                           placeholder="Eg. 2.2">
                    ${this._solidGMError ? html`<div class="error-message">${this._solidGMError}</div>` : nothing}
                </div>
            </div>
        </voyage-popup>
        `;
    }

    @property({ attribute: false }) private _forwardDraft: number = NaN;
    @property({attribute: false}) private _forwardDraftValid: boolean = false;
    @property({ attribute: false }) private _aftDraft: number = NaN;
    @property({attribute: false}) private _aftDraftValid: boolean = false;
    @property({ attribute: false }) private _forwardDraftRaw: string = '';
    @property({ attribute: false }) private _aftDraftRaw: string = '';
    @property({ attribute: false }) private _gmt: number = NaN;
    @property({attribute: false}) private _gmtValid: boolean = false;
    @property({ attribute: false }) private _solidGM: number = NaN;
    @property({attribute: false}) private _solidGMValid: boolean = false;
    @property({attribute: false}) private _isForwardDraftFromLoadstar: boolean = false;
    @property({attribute: false}) private _forwardDraftEdited: boolean = false;
    @property({attribute: false}) private _isAftDraftFromLoadstar: boolean = false;
    @property({attribute: false}) private _aftDraftEdited: boolean = false;
    @property({attribute: false}) private _isGmtFromLoadstar: boolean = false;
    @property({attribute: false}) private _gmtEdited: boolean = false;
    @property({attribute: false}) private _isSolidGMFromLoadstar: boolean = false;
    @property({attribute: false}) private _solidGMEdited: boolean = false;
    @property({ attribute: false }) private _gmtError: string = '';
    @property({ attribute: false }) private _solidGMError: string = '';
    @property({ attribute: false }) private _gmtRaw: string = '';
    @property({ attribute: false }) private _solidGMRaw: string = '';
    @property({ attribute: false }) private _working: boolean = false;
    @property({attribute: false}) private _isValid: boolean = false;
    private _scheduleId: string = "";
    private _shown: boolean = false;
    private _gmtTouched: boolean = false;
    private _solidGMTouched: boolean = false;
    private _forwardDraftTouched: boolean = false;
    private _aftDraftTouched: boolean = false;
    private _loadstarValues: LoadstarValues | null = null;

    updated(changedProperties: PropertyValues) {
        changedProperties.forEach((oldValue, propName) => {
            switch (propName) {
                case "_forwardDraft":
                case "_aftDraft":
                case "_gmt":
                    this._checkValidity()
                    break;
                case "_solidGM":
                    this._checkValidity()
                    break;
                default:
                    break;
            }
        })}
        
    private _checkValidity() {
        this._validateForwardDraft();
        this._validateAftDraft();
        this._validateGmt();
        this._validateSolidGM();
        this._isValid = this._forwardDraftValid && this._aftDraftValid && this._gmtValid && this._solidGMValid;
    }

    private _validateForwardDraft() {
        if (this._forwardDraftRaw.trim() === '' && !this._forwardDraftTouched && Number.isNaN(this._forwardDraft)) {
            this._forwardDraftValid = false;
        } else if (this._forwardDraftRaw.trim() === '') {
            this._forwardDraftValid = false;
        } else if (Number.isNaN(this._forwardDraft)) {
            this._forwardDraftValid = false;
        } else {
            this._forwardDraftValid = this._forwardDraft > 0 && this._forwardDraft <= 20;
        }
    }

    private _validateAftDraft() {
        if (this._aftDraftRaw.trim() === '' && !this._aftDraftTouched && Number.isNaN(this._aftDraft)) {
            this._aftDraftValid = false;
        } else if (this._aftDraftRaw.trim() === '') {
            this._aftDraftValid = false;
        } else if (Number.isNaN(this._aftDraft)) {
            this._aftDraftValid = false;
        } else {
            this._aftDraftValid = this._aftDraft > 0 && this._aftDraft <= 20;
        }
    }

    private _validateGmt() {
        this._gmtError = '';
        if (this._gmtRaw.trim() === '' && !this._gmtTouched && Number.isNaN(this._gmt)) {
            this._gmtValid = false;
        } else if (this._gmtRaw.trim() === '') {
            this._gmtError = 'Fluid GM is required.';
            this._gmtValid = false;
        } else if (Number.isNaN(this._gmt)) {
            this._gmtError = 'Please enter a valid number (meters).';
            this._gmtValid = false;
        } else if (this._gmt < 0.6 || this._gmt > 25) {
            this._gmtError = 'Fluid GM must be between 0.6 m and 25.0 m.';
            this._gmtValid = false;
        } else {
            this._gmtValid = true;
        }
    }

    private _validateSolidGM() {
        this._solidGMError = '';
        if (this._solidGMRaw.trim() === '' && !this._solidGMTouched && Number.isNaN(this._solidGM)) {
            this._solidGMValid = false;
        } else if (this._solidGMRaw.trim() === '') {
            this._solidGMError = 'Solid GM is required.';
            this._solidGMValid = false;
        } else if (Number.isNaN(this._solidGM)) {
            this._solidGMError = 'Please enter a valid number (meters).';
            this._solidGMValid = false;
        } else if (this._solidGM < 0.6 || this._solidGM > 25) {
            this._solidGMError = 'Solid GM must be between 0.6 m and 25.0 m.';
            this._solidGMValid = false;
        } else if (this._gmtValid && this._solidGM < this._gmt) {
            this._solidGMError = 'Solid GM must be greater than or equal to Fluid GM.';
            this._solidGMValid = false;
        } else if (this._gmtValid && (this._solidGM - this._gmt) > 5) {
            this._solidGMError = 'Solid GM must not exceed Fluid GM by more than 5.0 m.';
            this._solidGMValid = false;
        } else {
            this._solidGMValid = true;
        }
    }

    private _parseDecimal(value: string): number {
        const trimmed = value.trim();
        if (trimmed === '') return Number.NaN;
        const normalized = trimmed.replace(',', '.');
        const num = Number(normalized);
        return Number.isFinite(num) ? num : Number.NaN;
    }

    private _onGmtInput(evt: InputEvent) {
        this._gmtTouched = true;
        this._gmtRaw = (evt.target as HTMLInputElement).value;
        this._gmt = this._parseDecimal(this._gmtRaw);
        this._checkValidity();
    }

    private _onGmtBlur() {
        this._gmtTouched = true;
        this._checkValidity();
    }

    private _onSolidGMInput(evt: InputEvent) {
        this._solidGMTouched = true;
        this._solidGMRaw = (evt.target as HTMLInputElement).value;
        this._solidGM = this._parseDecimal(this._solidGMRaw);
        this._checkValidity();
    }

    private _onSolidGMBlur() {
        this._solidGMTouched = true;
        this._checkValidity();
    }

    private _onForwardDraftInput(evt: InputEvent) {
        this._forwardDraftTouched = true;
        this._forwardDraftRaw = (evt.target as HTMLInputElement).value;
        this._forwardDraft = this._parseDecimal(this._forwardDraftRaw);
        this._checkValidity();
    }

    private _onForwardDraftBlur() {
        this._forwardDraftTouched = true;
        this._checkValidity();
    }

    private _onAftDraftInput(evt: InputEvent) {
        this._aftDraftTouched = true;
        this._aftDraftRaw = (evt.target as HTMLInputElement).value;
        this._aftDraft = this._parseDecimal(this._aftDraftRaw);
        this._checkValidity();
    }

    private _onAftDraftBlur() {
        this._aftDraftTouched = true;
        this._checkValidity();
    }

    private async _readPlan(): Promise<void>
    {
        this._forwardDraft = NaN;
        this._aftDraft = NaN;
        this._gmt = NaN;
        this._solidGM = NaN;
        this._forwardDraftRaw = '';
        this._aftDraftRaw = '';
        this._gmtRaw = '';
        this._solidGMRaw = '';
        this._forwardDraftTouched = false;
        this._aftDraftTouched = false;
        this._gmtTouched = false;
        this._solidGMTouched = false;
        this._gmtError = '';
        this._solidGMError = '';
        this._forwardDraftEdited = false;
        this._aftDraftEdited = false;
        this._gmtEdited = false;
        this._solidGMEdited = false;
        this._isForwardDraftFromLoadstar = false;
        this._isAftDraftFromLoadstar = false;
        this._isGmtFromLoadstar = false;
        this._isSolidGMFromLoadstar = false;

        if (this._scheduleId)
        {
            this._working = true;

            // Fetch LoadStar values if vessel exists
            try {
                const vesselExistsInLoadstar = await LoadstarModel?.currentImoLoadstarExists();
                if (vesselExistsInLoadstar) {
                    const schedule = await VoyageSchedulesModel!.getScheduleFor(VoyageImoModel!.imo!);
                    const scheduleEntry = schedule?.find(entry => entry.id === this._scheduleId);
                    const voyageNumber = scheduleEntry?.departurePortCall?.GSIS?.departureVoyage || scheduleEntry?.arrivalPortCall?.GSIS?.departureVoyage;

                    this._loadstarValues = (await LoadstarModel?.getValueFromLoadstar(VoyageImoModel!.imo!, this._scheduleId, voyageNumber)) ?? null;
                    console.info("Fetched LoadStar values for voy-drafts-gmt-popup:", this._loadstarValues);
                } else {
                    this._loadstarValues = null;
                }
            } catch (error) {
                console.warn("Failed to fetch LoadStar values:", error);
                this._loadstarValues = null;
            }

            const plan = await VesselPlanningModel!.getCurrentPlan(this._scheduleId);
            if (plan)
            {
                // Forward Draft with fallback to LoadStar
                const loadstarForeDraft = this._loadstarValues?.fore_draft != null ? roundToDigits(this._loadstarValues.fore_draft, 2) : undefined;
                this._forwardDraft = plan.foreDraft < 0 ? (loadstarForeDraft ?? NaN) : plan.foreDraft;
                this._forwardDraftRaw = isNaN(this._forwardDraft) ? '' : String(this._forwardDraft);

                // Aft Draft with fallback to LoadStar
                const loadstarAftDraft = this._loadstarValues?.aft_draft != null ? roundToDigits(this._loadstarValues.aft_draft, 2) : undefined;
                this._aftDraft = plan.aftDraft < 0 ? (loadstarAftDraft ?? NaN) : plan.aftDraft;
                this._aftDraftRaw = isNaN(this._aftDraft) ? '' : String(this._aftDraft);

                // Fluid GM with fallback to LoadStar
                const loadstarFluidGm = this._loadstarValues?.fluid_gm != null ? roundToDigits(this._loadstarValues.fluid_gm, 2) : undefined;
                this._gmt = plan.gmt === null ? (loadstarFluidGm ?? NaN) : plan.gmt;
                this._gmtRaw = isNaN(this._gmt) ? '' : String(this._gmt);

                // Solid GM with fallback to LoadStar
                const loadstarSolidGm = this._loadstarValues?.solid_gm != null ? roundToDigits(this._loadstarValues.solid_gm, 2) : undefined;
                this._solidGM = (plan.solidGm === null || plan.solidGm === undefined) ? (loadstarSolidGm ?? NaN) : plan.solidGm;
                this._solidGMRaw = isNaN(this._solidGM) ? '' : String(this._solidGM);

                // Restore provenance flags from the persisted bitmask (same approach as voyage-leg-details).
                // On first open (mask is null/undefined) fall back to inferring from value availability,
                // since the field is currently displaying the loadstar fallback value.
                const savedSource = decodeLoadstarSource(plan.passagePlan.userInput?.loadstarSourceMask);
                this._isForwardDraftFromLoadstar = savedSource.foreDraft || (plan.foreDraft < 0 && loadstarForeDraft !== undefined);
                this._isAftDraftFromLoadstar    = savedSource.aftDraft  || (plan.aftDraft < 0 && loadstarAftDraft !== undefined);
                this._isGmtFromLoadstar         = savedSource.fluidGm   || (plan.gmt === null && loadstarFluidGm !== undefined);
                this._isSolidGMFromLoadstar     = savedSource.solidGm   || ((plan.solidGm === null || plan.solidGm === undefined) && loadstarSolidGm !== undefined);
            }

            this._working = false;
        }
    }

    private async _applyChange(): Promise<void>
    {
        this._working = true;

        try
        {
            const plan = await VesselPlanningModel!.getCurrentPlan(this._scheduleId);
            if (plan)
            {
                if (this._forwardDraft !== plan.foreDraft || this._aftDraft !== plan.aftDraft || this._gmt !== plan.gmt || this._solidGM !== plan.solidGm)
                {
                    plan.foreDraft = this._forwardDraft;
                    plan.aftDraft = this._aftDraft;
                    plan.gmt = this._gmt;
                    if ("solidGm" in plan) {
                        plan.solidGm = this._solidGM;
                    }
                    const existingSource = decodeLoadstarSource(plan.passagePlan.userInput?.loadstarSourceMask);
                    plan.passagePlan.userInput = {
                        ...plan.passagePlan.userInput,
                        loadstarSourceMask: encodeLoadstarSource({
                            foreDraft: this._isForwardDraftFromLoadstar && !this._forwardDraftEdited,
                            aftDraft:  this._isAftDraftFromLoadstar && !this._aftDraftEdited,
                            fluidGm:   this._isGmtFromLoadstar && !this._gmtEdited,
                            solidGm:   this._isSolidGMFromLoadstar && !this._solidGMEdited,
                            rollAngle: existingSource.rollAngle,
                            reefers:   existingSource.reefers,
                        }),
                    };

                    if (this._shouldWarn(plan))
                    {
                        VoyageDialogPopupManager.createDialog({
                            headerText: "Drafts changed",
                            bodyText: "Please ensure ECDIS Safety Contour check is conducted with trimmed drafts",
                            noCloseButton: true
                        });    
                    }

                    await recalcRoutes(plan);
                }
            }
            this.dispatchEvent(new Event("close"));
        }
        catch (err)
        {
            console.warn("Updating schedule and/or plan failed: ", err)
            VoyageDialogPopupManager.createDialog({
                headerText: "Failed to update",
                bodyText: "Unknown error occurred when applying the change",
                isError: true,
                errorDetails: err instanceof Error ? err.message : String(err)
            });
        }
        this._working = false;
    }

    private _shouldWarn(plan: VoyagePlan): boolean
    {
        if (plan.status !== "approved")
        {
            return false;
        }

        let shouldWarn = false;
        let ukcLimit = Math.max(0.5*plan.aftDraft, 0.5*plan.foreDraft, 3.5);

        // Check UKC from departure pilot to arrival pilot
        const pilotPoints: number[] = [];
        for (let i = 0; i < plan.routes[plan.selectedRouteIndex].route_geometry.length; i++)
        {
            const feature = plan.routes[plan.selectedRouteIndex].route_geometry[i];
            if (feature.geometry.type === "Point" && feature.properties.pilotPoint)
            {
                pilotPoints.push(i);
            }
        }

        let startIndex = 0;
        let endIndex = plan.routes[plan.selectedRouteIndex].route_geometry.length-1;
        if (pilotPoints.length >= 2)
        {
            startIndex = pilotPoints[0];
            endIndex = pilotPoints[pilotPoints.length-1];
        }

        const draft = Math.max(plan.aftDraft, plan.foreDraft)
        for (let i = startIndex; i < endIndex; i++)
        {
            const feature = plan.routes[plan.selectedRouteIndex].route_geometry[i];
            if (feature.geometry.type === "Point" && feature.properties.type === "Hard")
            {
                const minDepth = feature.properties.userInput?.min_depth ?? feature.properties.min_depth;
                const tide = feature.properties.tide ?? feature.properties.userInput?.tide ?? 0;
                const ukc = (minDepth ?? NaN) + tide - draft;
                if (isNaN(ukc) || ukc < ukcLimit)
                {
                    // Neeeded data for UKC calculation is missing or UKC is below the limit
                    shouldWarn = true;
                    break;
                }
            }
        }

        return shouldWarn;
    }
}
